"use strict";

import { loadImage, ready } from "./lib/promises";
import { Board } from "./lib/board";
import MipMap from "./lib/MipMap";

/**
 * Zoom and Pan properties
 */
const scaleStepFactor = Math.sqrt(0.5);
const scaleMin = 12;
const scaleMax = -6;
let scaleFalse = 0;
let scaleTrue = scaleFalse;
const scaleDuration = 200;
let scaleTime = 0;

let mapDX = 0;
let mapDY = 0;
let trueDX = mapDX;
let trueDY = mapDY;

/**
 * Math constants
 */
const SQRT3 = Math.sqrt(3);
const SCALEY = Math.SQRT2 / SQRT3;

/**
 * Helper functions
 */
const mod = (x, n) => ((x % n) + n) % n;

const tiles = [
	{ id: "neutral", name: "Neutral Panel", placeable: true },
	{ id: "bonus", name: "Bonus", x2: "bonusx2", placeable: true },
	{ id: "roll", name: "Roll and Move", x2: "rollx2", placeable: true },
	{ id: "encounter", name: "Encounter", x2: "encounterx2", placeable: true },
	{ id: "heal", name: "Heal", x2: "healx2", placeable: true },

	{ id: "home", name: "Home", placeable: true },
	{ id: "bonusx2", name: "Bonus ×2", placeable: true },
	{ id: "rollx2", name: "Roll and Move ×2", placeable: true },
	{ id: "encounterx2", name: "Encounter ×2", placeable: true },
	{ id: "healx2", name: "Heal ×2", placeable: true },

	{ id: "draw", name: "Draw", x2: "drawx2", placeable: true },
	{ id: "drop", name: "Drop", x2: "dropx2", placeable: true },
	{
		id: "warpmove",
		name: "Warp and Move",
		x2: "warpmovex2",
		placeable: true
	},
	{ id: "boss", name: "Boss", placeable: true },
	{ id: "damage", name: "Damage", placeable: true },

	{ id: "drawx2", name: "Draw ×2", placeable: true },
	{ id: "dropx2", name: "Drop ×2", placeable: true },
	{ id: "warpmovex2", name: "Warp and Move ×2", placeable: true },
	{ id: "coopboss", name: "Co-op Boss", placeable: true },
	{ id: "damagex2", name: "Damage ×2", placeable: true },

	{ id: "ice", name: "Ice Panel", placeable: false },
	{ id: "snow", name: "Snow", placeable: true },
	{ id: "warp", name: "Warp", placeable: true },
	{ id: "event", name: "Event Panel", placeable: true },
	{ id: "null", name: "null", placeable: false }
];

for (let i = 0; i < tiles.length; ++i) {
	const x = i % 5;
	tiles[i].coords = { x, y: (i - x) / 5 };
}

const arrows = [
	{ id: "right", name: "Right", placeable: false },
	{ id: "up", name: "Up", placeable: false },
	{ id: "left", name: "left", placeable: false },
	{ id: "down", name: "Down", placeable: false }
];

for (let i = 0; i < arrows.length; ++i) {
	arrows[i].coords = { x: i, y: 0 };
}

const tileByID = {};
for (const tile of tiles) {
	tileByID[tile.id] = tile;
}

for (const arrow of arrows) {
	tileByID[arrow.id] = arrow;
}

const placeable = tiles.filter(tile => tile.placeable);
const arrowXOR = [0b0001, 0b0010, 0b0100, 0b1000];
const arrowOffsets = [
	{ x: 64, y: 0 },
	{ x: 0, y: -64 },
	{ x: -64, y: 0 },
	{ x: 0, y: 64 }
];

let gameBoard = null;
let arrowBoard = null;
try {
	const { width, height, data } = JSON.parse(localStorage["board"]);
	gameBoard = new Board(width, height, data);
} catch (e) {
	gameBoard = new Board(3, 3, [
		"home",
		"draw",
		"home",
		"bonus",
		"",
		"bonus",
		"home",
		"draw",
		"home"
	]);
}

try {
	const { data } = JSON.parse(localStorage["arrow2"]);
	arrowBoard = new Board(gameBoard.width, gameBoard.height, data);
} catch (e) {
	arrowBoard = new Board(gameBoard.width, gameBoard.height);
}

const TILES_PROMISE = loadImage("tiles2.png");
const MASS_PROMISE = loadImage("mass.png");
const ARROWS_PROMISE = loadImage("arrows.png");

const ARROW_PERSPECTIVE = document.createElement("canvas");
const TILE_PERSPECTIVE = document.createElement("canvas");
const A_P_CONTEXT = ARROW_PERSPECTIVE.getContext("2d");
const T_P_CONTEXT = TILE_PERSPECTIVE.getContext("2d");
{
	ARROW_PERSPECTIVE.width = Math.ceil(16 * 4 * Math.SQRT2);
	ARROW_PERSPECTIVE.height = Math.ceil(16 * SCALEY);

	TILE_PERSPECTIVE.width = Math.ceil(128 * 5 * Math.SQRT2);
	TILE_PERSPECTIVE.height = Math.ceil(128 * 5 * SCALEY) + 5 * 8;

	A_P_CONTEXT.translate(ARROW_PERSPECTIVE.width / 2, 0);
	A_P_CONTEXT.scale(1, 1 / SQRT3);
	A_P_CONTEXT.rotate(Math.PI / 4);

	T_P_CONTEXT.translate(TILE_PERSPECTIVE.width / 2, 0);
	T_P_CONTEXT.scale(1, 1 / SQRT3);
	T_P_CONTEXT.rotate(Math.PI / 4);
}

const canvas = document.createElement("canvas");
const context = canvas.getContext("2d");

const overlay = document.createElement("div");
overlay.id = "overlay";
overlay.hidden = true;

const closeOverlay = () => (overlay.hidden = true);

const close = overlay.appendChild(document.createElement("div"));
close.id = "close";
close.addEventListener("click", closeOverlay);

const prompt = document.createElement("div");
prompt.id = "prompt";

const promptText = prompt.appendChild(document.createElement("span"));
promptText.id = "prompt-message";

const promptCancel = prompt.appendChild(document.createElement("div"));
promptCancel.id = "prompt-cancel";
promptCancel.classList.add("button", "cancel");
promptCancel.appendChild(document.createTextNode("Cancel"));
promptCancel.addEventListener("click", closeOverlay);

const promptOk = prompt.appendChild(document.createElement("div"));
promptOk.id = "prompt-ok";
promptOk.classList.add("button");

let promptOkListener = null;
promptOk.addEventListener("click", e => {
	if (promptOkListener !== null) promptOkListener();
});

window.addEventListener("keypress", e => {
	if (!e.key) return;
	if (e.key === "r") {
		while (overlay.firstChild) overlay.removeChild(overlay.firstChild);
		overlay.appendChild(close);
		promptText.textContent = "Are you sure you want to reset the board?";
		promptOk.textContent = "Reset";
		promptOkListener = () => {
			localStorage.removeItem("board");
			localStorage.removeItem("arrow2");
			location.reload();
		};

		overlay.appendChild(prompt);
		overlay.hidden = false;
	}
});

async function main() {
	canvas.width = 128;
	canvas.height = 128;
	context.fillStyle = "#fed";
	context.fillRect(0, 0, 64, 64);
	context.fillRect(64, 64, 64, 64);
	context.fillStyle = "#edc";
	context.fillRect(0, 64, 64, 64);
	context.fillRect(64, 0, 64, 64);

	const pattern = context.createPattern(canvas, "repeat");

	const [TILE_ART, MASS_ART, ARROWS_ART] = await Promise.all([
		TILES_PROMISE,
		MASS_PROMISE,
		ARROWS_PROMISE
	]);

	const TILE_MIPMAP = new MipMap(TILE_ART);

	for (const {
		coords: { x, y }
	} of arrows) {
		A_P_CONTEXT.drawImage(
			ARROWS_ART,
			x * 16,
			0,
			16,
			16,
			(x - 1.5 + y) * 16,
			(y + 1.5 - x) * 16,
			16,
			16
		);
	}

	for (const tile of tiles) {
		const x = tile.coords.x;
		const y = tile.coords.y;

		let dx = (x - 2 + y) * 128;
		let dy = (y + 2 - x) * 128;

		dx += y * 8;
		dy += y * 8;

		if (tile.placeable) T_P_CONTEXT.drawImage(MASS_ART, dx - 11, dy - 11);

		T_P_CONTEXT.drawImage(
			TILE_ART,
			x * 128,
			y * 128,
			128,
			128,
			dx,
			dy,
			128,
			128
		);
	}

	const ISOMETRIC_MIPMAP = new MipMap(TILE_PERSPECTIVE);

	let blob = "";
	let selectedTile = "neutral";

	let mouseState = "";
	let mouseCoords = { x: 0, y: 0 };
	let dragCoords = { x: 0, y: 0 };
	canvas.addEventListener("contextmenu", e => {
		e.preventDefault();
		const scale = scaleStepFactor ** scaleFalse;
		const x = e.offsetX - canvas.width / 2;
		const y = e.offsetY - canvas.height / 2;

		let tileX = Math.floor(
			gameBoard.width / 2 + (x - mapDX * scale) / scale / 128
		);
		let tileY = Math.floor(
			gameBoard.height / 2 + (y - mapDY * scale) / scale / 128
		);

		let w = gameBoard.width;
		let h = gameBoard.height;

		if (tileX < 0 || tileX >= w || tileY < 0 || tileY >= h) return;

		gameBoard.setTile(tileX, tileY, "");
		let t = gameBoard.height;
		let r = 0;
		let b = 0;
		let l = gameBoard.width;
		let tile = "";
		for (let y = 0; y < gameBoard.height; ++y) {
			for (let x = 0; x < gameBoard.width; ++x) {
				tile = gameBoard.getTile(x, y);
				if (tile === "") continue;
				if (y < t) t = y;
				if (x < l) l = x;
				if (y > b) b = y;
				if (x > r) r = x;
			}
		}

		let w2 = r - l + 1;
		let h2 = b - t + 1;
		if (w !== w2 || h !== h2) {
			gameBoard.resize(w2, h2, l, t);
			arrowBoard.resize(w2, h2, l, t);
			trueDX += (w2 - w + l * 2) * 64;
			trueDY += (h2 - h + t * 2) * 64;
		}

		localStorage["board"] = JSON.stringify(gameBoard);
		localStorage["arrow2"] = JSON.stringify(arrowBoard);
	});

	canvas.addEventListener("mousedown", event => {
		if (event.button !== 0) return;
		event.preventDefault();
		mouseState = "down";
		mouseCoords.x = event.offsetX;
		mouseCoords.y = event.offsetY;
		dragCoords.x = event.clientX;
		dragCoords.y = event.clientY;
	});

	window.addEventListener("mousemove", event => {
		if (mouseState === "") return;
		const scale = scaleStepFactor ** scaleFalse;

		let dx = dragCoords.x - event.clientX;
		let dy = dragCoords.y - event.clientY;

		if (Math.hypot(dx, dy) <= 0.01) return;
		mouseState = "drag";

		dragCoords.x = event.clientX;
		dragCoords.y = event.clientY;

		trueDX -= dx / scale;
		trueDY -= dy / scale;
	});

	window.addEventListener("mouseup", e => {
		if (mouseState === "") return;
		try {
			if (mouseState === "down") {
				/**
				 * Select Tile
				 */
				if (
					e.offsetX > 8 &&
					e.offsetX < 32 * 5 + 8 &&
					e.offsetY > 8 &&
					e.offsetY < 32 * 5 + 8
				) {
					const selected =
						Math.floor((e.offsetX - 8) / 32) +
						Math.floor((e.offsetY - 8) / 32) * 5;

					if (placeable[selected]) {
						selectedTile = placeable[selected].id;
					}

					return;
				}

				/**
				 * Select Arrow
				 */
				if (
					e.offsetX > 32 * 5 + 8 &&
					e.offsetX < 32 * 6 + 8 &&
					e.offsetY > 8 &&
					e.offsetY < 32 * 4 + 8
				) {
					const selected = Math.floor((e.offsetY - 8) / 32);
					selectedTile = arrows[selected].id;
					return;
				}

				/**
				 * Open Preview
				 */
				if (
					e.offsetX > 8 &&
					e.offsetX < 86 &&
					e.offsetY > canvas.height - 40 &&
					e.offsetY < canvas.height - 8
				) {
					if (e.offsetX < 40) {
						canvas.width = gameBoard.width * 128;
						canvas.height = gameBoard.height * 128;

						let tileID = "";
						let tile = null;
						let arrowMap = 0;
						for (let x = 0; x < gameBoard.width; ++x) {
							for (let y = 0; y < gameBoard.height; ++y) {
								tileID = gameBoard.getTile(x, y);
								if (tileID === "") continue;
								tile = tileByID[tileID];
								if (!tile) continue;

								context.drawImage(
									TILE_ART,
									tile.coords.x << 7,
									tile.coords.y << 7,
									128,
									128,
									x << 7,
									y << 7,
									128,
									128
								);
							}
						}

						for (let x = 0; x < gameBoard.width; ++x) {
							for (let y = 0; y < gameBoard.height; ++y) {
								arrowMap = arrowBoard.getTile(x, y);
								if (arrowMap === 0) continue;

								for (let i = 0; i < arrows.length; ++i) {
									if ((arrowXOR[i] & arrowMap) === 0)
										continue;

									tile = arrows[i];
									const offset = arrowOffsets[i];

									context.drawImage(
										ARROWS_ART,
										tile.coords.x << 4,
										tile.coords.y << 4,
										16,
										16,
										(x << 7) + (offset.x + 56),
										(y << 7) + (offset.y + 56),
										16,
										16
									);
								}
							}
						}
					} else {
						let tile = null;
						let tileID = "";
						let arrowMap = 0;
						let top = gameBoard.height + gameBoard.width;
						let right = 0;
						let bottom = 0;
						let left = gameBoard.width;

						for (let y = 0; y < gameBoard.height; ++y) {
							for (let x = 0; x < gameBoard.width; ++x) {
								tileID = gameBoard.getTile(x, y);
								if (tileID !== "") {
									let dy = x + y;
									let dx = x - y;
									if (dy < top && tileID !== "") top = dy;
									if (dx < left && tileID !== "") left = dx;
									if (dy > bottom && tileID !== "")
										bottom = dy;
									if (dx > right && tileID !== "") right = dx;
								}
							}
						}

						canvas.width = Math.ceil(
							64 * (right - left + 2) * Math.SQRT2
						);
						canvas.height =
							Math.ceil(64 * (bottom - top + 2) * SCALEY) + 8;

						for (let x = 0; x < gameBoard.width; ++x) {
							for (let y = 0; y < gameBoard.height; ++y) {
								tileID = gameBoard.getTile(x, y);
								if (tileID === "") continue;
								tile = tileByID[tileID];
								if (!tile) continue;

								context.drawImage(
									TILE_PERSPECTIVE,
									tile.coords.x * 128 * Math.SQRT2,
									tile.coords.y * (128 + 8) * SCALEY,
									128 * Math.SQRT2,
									128 * SCALEY + 8,
									(x - y - left) * 64 * Math.SQRT2,
									(y + x - top) * 64 * SCALEY,
									128 * Math.SQRT2,
									128 * SCALEY + 8
								);
							}
						}

						left *= 128;
						top *= 128;

						for (let x = 0; x < gameBoard.width; ++x) {
							for (let y = 0; y < gameBoard.height; ++y) {
								arrowMap = arrowBoard.getTile(x, y);
								if (arrowMap === 0) continue;

								for (let i = 0; i < arrows.length; ++i) {
									if ((arrowXOR[i] & arrowMap) === 0)
										continue;
									tile = arrows[i];

									const w = 16 * Math.SQRT2;
									const h = 16 * SCALEY + 8;

									const offset = arrowOffsets[i];
									let x2 = x * 128 + offset.x + 128 - 16;
									let y2 = y * 128 + offset.y;

									context.drawImage(
										ARROW_PERSPECTIVE,
										(tile.coords.x << 4) * Math.SQRT2,
										tile.coords.y * (16 + 8) * SCALEY,
										w,
										h,
										((x2 - y2 - left) * Math.SQRT2) / 2,
										((y2 + x2 - top) * SCALEY) / 2,
										w,
										h
									);
								}
							}
						}
					}

					canvas.toBlob(blobObject => {
						if (blob !== "") URL.revokeObjectURL(blob);

						while (overlay.firstChild)
							overlay.removeChild(overlay.firstChild);

						blob = URL.createObjectURL(blobObject);
						const image = new Image();
						image.src = blob;
						overlay.appendChild(close);
						overlay.appendChild(image);
						overlay.hidden = false;
					}, "image/png");

					resize();
					return;
				}

				const scale = scaleStepFactor ** scaleFalse;
				const x = e.offsetX - canvas.width / 2;
				const y = e.offsetY - canvas.height / 2;

				let tileX = Math.floor(
					gameBoard.width / 2 + (x - mapDX * scale) / scale / 128
				);
				let tileY = Math.floor(
					gameBoard.height / 2 + (y - mapDY * scale) / scale / 128
				);

				let w = gameBoard.width;
				let h = gameBoard.height;

				let arrowIndex = arrows.indexOf(tileByID[selectedTile]);
				if (arrowIndex >= 0) {
					if (tileX < 0 || tileX >= w || tileY < 0 || tileY >= h)
						return;
					arrowBoard.setTile(
						tileX,
						tileY,
						arrowBoard.getTile(tileX, tileY) ^ arrowXOR[arrowIndex]
					);

					localStorage["arrow2"] = JSON.stringify(arrowBoard);
					return;
				}

				let dx = 0;
				let dy = 0;

				if (tileX < 0) {
					trueDX += 64 * tileX;
					dx += tileX;
					w -= tileX;
					tileX = 0;
				} else if (tileX >= w) {
					trueDX += 64 * (tileX - w + 1);
					w = tileX + 1;
				}

				if (tileY < 0) {
					trueDY += 64 * tileY;
					dy += tileY;
					h -= tileY;
					tileY = 0;
				} else if (tileY >= h) {
					trueDY += 64 * (tileY - h + 1);
					h = tileY + 1;
				}

				if (w < 1) w = 1;
				if (h < 1) h = 1;

				if (w !== gameBoard.width || h !== gameBoard.height) {
					gameBoard.resize(w, h, dx, dy);
					arrowBoard.resize(w, h, dx, dy);
				}

				gameBoard.setTile(tileX, tileY, selectedTile);
				localStorage["board"] = JSON.stringify(gameBoard);
				localStorage["arrow2"] = JSON.stringify(arrowBoard);
			}
		} catch (e) {
			console.warn(e);
		} finally {
			mouseState = "";
		}
	});

	window.addEventListener("blur", () => {
		if (mouseState === "") return;
		mouseState = "";
	});

	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};

	window.addEventListener("resize", resize);
	resize();

	const zoomAt = (x, y, steps) => {
		const old = scaleTrue;
		const oldScale = scaleStepFactor ** scaleFalse;

		scaleTrue = Math.max(scaleMax, Math.min(scaleMin, scaleTrue - steps));
		if (old === scaleTrue) return;

		scaleTime = scaleDuration;

		const trueScale = scaleStepFactor ** scaleTrue;
		const delta = 1 / trueScale - 1 / oldScale;

		trueDX = mapDX + x * delta;
		trueDY = mapDY + y * delta;
	};

	canvas.addEventListener("wheel", event => {
		if (event.ctrlKey) return;
		event.preventDefault();

		const scrollSign = Math.sign(event.deltaY) * -1;
		let scrollSteps = scrollSign;
		switch (event.deltaMode) {
			case 0:
				break;
			case 1:
				scrollSteps *= 2;
				break;
			case 2:
				scrollSteps *= 5;
				break;
		}

		zoomAt(
			event.offsetX - canvas.width / 2,
			event.offsetY - canvas.height / 2,
			scrollSteps
		);
	});

	let lastFrame = performance.now();
	const onFrame = () => {
		const now = performance.now();
		const delta = now - lastFrame;
		lastFrame = now;

		const oldScale = scaleStepFactor ** scaleFalse;

		scaleTime = Math.max(0, scaleTime - delta);
		scaleFalse =
			scaleTrue - ((scaleTrue - scaleFalse) * scaleTime) / scaleDuration;

		const scale = scaleStepFactor ** scaleFalse;

		if (trueDX !== mapDX || trueDY !== mapDY) {
			const trueScale = scaleStepFactor ** scaleTrue;
			const delta = 1 / trueScale - 1 / oldScale;

			if (delta !== 0) {
				const sx = (trueDX - mapDX) / delta;
				const sy = (trueDY - mapDY) / delta;

				const newDelta = 1 / scale - 1 / oldScale;

				mapDX += sx * newDelta;
				mapDY += sy * newDelta;
			} else {
				mapDX = trueDX;
				mapDY = trueDY;
			}
		}

		context.setTransform(
			scale,
			0,
			0,
			scale,
			(mod(canvas.width / 2 / scale + mapDX - gameBoard.width * 64, 256) -
				256) *
				scale,
			(mod(
				canvas.height / 2 / scale + mapDY - gameBoard.height * 64,
				256
			) -
				256) *
				scale
		);

		context.fillStyle = pattern;
		context.fillRect(
			0,
			0,
			256 + canvas.width / scale,
			256 + canvas.height / scale
		);

		/**
		 * Main map
		 */
		context.setTransform(1, 0, 0, 1, 0, 0);

		let tile = null;
		let tileID = "";
		let arrowMap = 0;

		const tileSize = 128 * scale;
		const gdx = scale * (mapDX - gameBoard.width * 64) + canvas.width / 2;
		const gdy = scale * (mapDY - gameBoard.height * 64) + canvas.height / 2;

		// TILES
		for (let x = 0; x < gameBoard.width; ++x) {
			for (let y = 0; y < gameBoard.height; ++y) {
				tileID = gameBoard.getTile(x, y);
				if (tileID === "") continue;

				tile = tileByID[tileID];
				if (!tile) continue;

				let sx = tile.coords.x << 7;
				let sy = tile.coords.y << 7;
				const sw = 128;
				const sh = 128;

				let dx = gdx + tileSize * x;
				let dy = gdy + tileSize * y;
				const dw = tileSize;
				const dh = tileSize;

				// scale
				TILE_MIPMAP.draw(context, sx, sy, sw, sh, dx, dy, dw, dh);
			}
		}

		// ARROWS
		for (let x = 0; x < gameBoard.width; ++x) {
			for (let y = 0; y < gameBoard.height; ++y) {
				arrowMap = arrowBoard.getTile(x, y);
				if (arrowMap === 0) continue;

				for (let i = 0; i < arrows.length; ++i) {
					if ((arrowXOR[i] & arrowMap) === 0) continue;
					tile = arrows[i];

					const offset = arrowOffsets[i];

					const sx = tile.coords.x << 4;
					const sy = tile.coords.y << 4;
					const dx = gdx + tileSize * x + (offset.x + 56) * scale;
					const dy = gdy + tileSize * y + (offset.y + 56) * scale;
					const sw = 16;
					const sh = 16;
					const dw = 16 * scale;
					const dh = 16 * scale;

					context.drawImage(
						ARROWS_ART,
						sx,
						sy,
						sw,
						sh,
						dx,
						dy,
						dw,
						dh
					);
				}
			}
		}

		/**
		 * Perspective preview (bottom right)
		 */
		let top = gameBoard.height + gameBoard.width;
		let right = 0;
		let bottom = 0;
		let left = gameBoard.width;
		for (let y = 0; y < gameBoard.height; ++y) {
			for (let x = 0; x < gameBoard.width; ++x) {
				tileID = gameBoard.getTile(x, y);
				if (tileID !== "") {
					let dy = x + y;
					let dx = x - y;
					if (dy < top && tileID !== "") top = dy;
					if (dx < left && tileID !== "") left = dx;
					if (dy > bottom && tileID !== "") bottom = dy;
					if (dx > right && tileID !== "") right = dx;
				}
			}
		}

		const width = Math.ceil(64 * (right - left + 2) * Math.SQRT2);
		const height = Math.ceil(64 * (bottom - top + 2) * SCALEY) + 8;

		const previewScale = Math.min(
			0.5,
			canvas.width / 3 / width,
			canvas.height / 3 / height
		);

		context.setTransform(
			previewScale,
			0,
			0,
			previewScale,
			canvas.width - width * previewScale - 8,
			canvas.height - height * previewScale - 8
		);

		// TILES
		for (let x = 0; x < gameBoard.width; ++x) {
			for (let y = 0; y < gameBoard.height; ++y) {
				tileID = gameBoard.getTile(x, y);
				if (tileID === "") continue;
				tile = tileByID[tileID];
				if (!tile) continue;

				ISOMETRIC_MIPMAP.draw(
					context,
					tile.coords.x * 128 * Math.SQRT2,
					tile.coords.y * (128 + 8) * SCALEY,
					128 * Math.SQRT2,
					128 * SCALEY + 8,
					(x - y - left) * 64 * Math.SQRT2,
					(y + x - top) * 64 * SCALEY,
					128 * Math.SQRT2,
					128 * SCALEY + 8
				);
			}
		}

		left *= 128;
		top *= 128;

		// ARROWS
		for (let x = 0; x < gameBoard.width; ++x) {
			for (let y = 0; y < gameBoard.height; ++y) {
				arrowMap = arrowBoard.getTile(x, y);
				if (arrowMap === 0) continue;

				for (let i = 0; i < arrows.length; ++i) {
					if ((arrowXOR[i] & arrowMap) === 0) continue;
					tile = arrows[i];

					const w = 16 * Math.SQRT2;
					const h = 16 * SCALEY + 8;

					const offset = arrowOffsets[i];
					let x2 = x * 128 + offset.x + 128 - 16;
					let y2 = y * 128 + offset.y;

					context.drawImage(
						ARROW_PERSPECTIVE,
						(tile.coords.x << 4) * Math.SQRT2,
						tile.coords.y * (16 + 8) * SCALEY,
						w,
						h,
						((x2 - y2 - left) * Math.SQRT2) / 2,
						((y2 + x2 - top) * SCALEY) / 2,
						w,
						h
					);
				}
			}
		}

		/**
		 * Tile selector (top left)
		 */
		context.setTransform(1, 0, 0, 1, 0, 0);
		for (let i = 0; i < placeable.length; ++i) {
			tile = placeable[i];
			let x = i % 5;
			let y = (i - x) / 5;
			x = x * 32 + 8;
			y = y * 32 + 8;

			if (tile.id === selectedTile) {
				TILE_MIPMAP.draw(
					context,
					tile.coords.x << 7,
					tile.coords.y << 7,
					128,
					128,
					x,
					y,
					32,
					32
				);
				continue;
			}

			TILE_MIPMAP.draw(
				context,
				tile.coords.x << 7,
				tile.coords.y << 7,
				128,
				128,
				x + 4,
				y + 4,
				32 - 8,
				32 - 8
			);
		}

		let tempTile = (tile = tileByID["neutral"]);
		for (let i = 0; i < arrows.length; ++i) {
			tile = arrows[i];
			let x = 5 * 32 + 8;
			let y = i * 32 + 8;

			if (tile.id === selectedTile) {
				TILE_MIPMAP.draw(
					context,
					tempTile.coords.x << 7,
					tempTile.coords.y << 7,
					128,
					128,
					x,
					y,
					32,
					32
				);
			} else {
				TILE_MIPMAP.draw(
					context,
					tempTile.coords.x << 7,
					tempTile.coords.y << 7,
					128,
					128,
					x + 4,
					y + 4,
					32 - 8,
					32 - 8
				);
			}

			context.drawImage(
				ARROWS_ART,
				i * 16,
				0,
				16,
				16,
				x + 8,
				y + 8,
				16,
				16
			);
		}

		/**
		 * Render buttons (bottom left)
		 */
		{
			tile = tileByID["neutral"];

			TILE_MIPMAP.draw(
				context,
				tile.coords.x << 7,
				tile.coords.y << 7,
				128,
				128,
				8,
				canvas.height - 40,
				32,
				32
			);

			ISOMETRIC_MIPMAP.draw(
				context,
				tile.coords.x * 128 * Math.SQRT2,
				tile.coords.y * (128 + 8) * SCALEY,
				128 * Math.SQRT2,
				128 * SCALEY + 8,
				40,
				canvas.height - 32 * SCALEY - 14,
				32 * Math.SQRT2,
				32 * SCALEY + 8
			);
		}

		requestAnimationFrame(onFrame);
	};

	onFrame();

	await ready;
	document.body.appendChild(canvas);
	document.body.appendChild(overlay);
}

main().catch(e => console.warn(e));
